import React, { useState, useEffect } from 'react'
import {
  XMarkIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  SparklesIcon,
  ShieldCheckIcon,
  BoltIcon,
  CreditCardIcon,
  BanknotesIcon,
  QrCodeIcon
} from '@heroicons/react/24/outline'
import { paymentGatewayAPI } from '@/services/api'

interface AsaasConfigModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  existingGateway?: any // Para edição
}

interface FormData {
  gateway_name: string
  api_key: string
  environment: 'sandbox' | 'production'
  enable_pix: boolean
  enable_boleto: boolean
  enable_credit_card: boolean
  enable_debit_card: boolean
  enable_split: boolean
  webhook_url: string
  auto_sync: boolean
}

interface ValidationErrors {
  [key: string]: string
}

const AsaasConfigModal: React.FC<AsaasConfigModalProps> = ({ isOpen, onClose, onSuccess, existingGateway }) => {
  const [step, setStep] = useState(1) // 1: Config, 2: Features, 3: Test

  const [formData, setFormData] = useState<FormData>({
    gateway_name: '',
    api_key: '',
    environment: 'sandbox',
    enable_pix: true,
    enable_boleto: true,
    enable_credit_card: true,
    enable_debit_card: false,
    enable_split: false,
    webhook_url: '',
    auto_sync: true
  })
  const [errors, setErrors] = useState<ValidationErrors>({})
  const [testing, setTesting] = useState(false)
  const [saving, setSaving] = useState(false)
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null)

  // Reset state when modal closes OR populate with existing gateway data
  useEffect(() => {
    if (!isOpen) {
      setStep(1)
      setFormData({
        gateway_name: '',
        api_key: '',
        environment: 'sandbox',
        enable_pix: true,
        enable_boleto: true,
        enable_credit_card: true,
        enable_debit_card: false,
        enable_split: false,
        webhook_url: '',
        auto_sync: true
      })
      setErrors({})
      setTestResult(null)
    } else if (existingGateway) {
      // Modo edição - preencher formulário com dados existentes
      const config = typeof existingGateway.config === 'string'
        ? JSON.parse(existingGateway.config)
        : existingGateway.config || {}

      setFormData({
        gateway_name: existingGateway.name || existingGateway.gateway_name || '',
        api_key: '', // Por segurança, não preencher a API key
        environment: existingGateway.environment || 'sandbox',
        enable_pix: config.enable_pix !== undefined ? config.enable_pix : true,
        enable_boleto: config.enable_boleto !== undefined ? config.enable_boleto : true,
        enable_credit_card: config.enable_credit_card !== undefined ? config.enable_credit_card : true,
        enable_debit_card: config.enable_debit_card !== undefined ? config.enable_debit_card : false,
        enable_split: config.enable_split !== undefined ? config.enable_split : false,
        webhook_url: existingGateway.webhook_url || config.webhook_url || '',
        auto_sync: existingGateway.auto_sync !== undefined ? existingGateway.auto_sync : true
      })
      // Modo edição: voltar para step 1, mas validação será diferente
    }
  }, [isOpen, existingGateway])

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {}

    if (!formData.gateway_name.trim()) {
      newErrors.gateway_name = 'Nome da integração é obrigatório'
    }

    // Em modo criação, API key é obrigatória
    // Em modo edição, é opcional (só valida se foi preenchida)
    if (!existingGateway) {
      if (!formData.api_key.trim()) {
        newErrors.api_key = 'API Key é obrigatória'
      } else if (formData.api_key.length < 30) {
        newErrors.api_key = 'API Key inválida (muito curta)'
      }
    } else {
      // Em modo edição, valida apenas se o usuário preencheu
      if (formData.api_key.trim() && formData.api_key.length < 30) {
        newErrors.api_key = 'API Key inválida (muito curta)'
      }
    }

    if (formData.webhook_url && !formData.webhook_url.startsWith('http')) {
      newErrors.webhook_url = 'URL de webhook inválida'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleTestConnection = async () => {
    if (!validateForm()) {
      return
    }
    setTesting(true)
    setTestResult(null)

    try {
      const gatewayConfig = {
        enable_pix: formData.enable_pix,
        enable_boleto: formData.enable_boleto,
        enable_credit_card: formData.enable_credit_card,
        enable_debit_card: formData.enable_debit_card,
        enable_split: formData.enable_split,
        webhook_url: formData.webhook_url,
        auto_sync: formData.auto_sync
      }

      const payload = {
        gateway_type: 'asaas',
        gateway_name: formData.gateway_name,
        api_key: formData.api_key,
        api_secret: '', // ASAAS não usa secret
        environment: formData.environment,
        webhook_secret: '',
        gateway_config: JSON.stringify(gatewayConfig) // Backend espera string JSON
      }

      const response = await paymentGatewayAPI.testConnection(payload)

      setTestResult({
        success: response.success,
        message: response.message || 'Conexão testada com sucesso!'
      })

      if (response.success) {
        setStep(2)
      }
    } catch (error: any) {
      console.error('Erro ao testar conexão:', error)
      setTestResult({
        success: false,
        message: error.response?.data?.detail || 'Erro ao testar conexão. Verifique suas credenciais.'
      })
    } finally {
      setTesting(false)
    }
  }

  const handleSave = async () => {
    // Em modo edição, validar apenas se API key foi informada (opcional)
    if (!existingGateway && !validateForm()) {
      return
    }

    // Validar campos básicos em modo edição
    if (existingGateway && !formData.gateway_name.trim()) {
      setErrors({ gateway_name: 'Nome da integração é obrigatório' })
      return
    }

    setSaving(true)

    try {
      const gatewayConfig = {
        enable_pix: formData.enable_pix,
        enable_boleto: formData.enable_boleto,
        enable_credit_card: formData.enable_credit_card,
        enable_debit_card: formData.enable_debit_card,
        enable_split: formData.enable_split,
        webhook_url: formData.webhook_url,
        auto_sync: formData.auto_sync
      }

      const payload: any = {
        gateway_type: 'asaas',
        gateway_name: formData.gateway_name,
        environment: formData.environment,
        gateway_config: JSON.stringify(gatewayConfig),
        is_active: true
      }

      // Só incluir API key se foi fornecida (importante para edição)
      if (formData.api_key.trim()) {
        payload.api_key = formData.api_key
        payload.api_secret = ''
        payload.webhook_secret = ''
      }

      if (existingGateway) {
        // Modo EDIÇÃO - atualizar gateway existente
        await paymentGatewayAPI.updateGateway(existingGateway.id.toString(), payload)
      } else {
        // Modo CRIAÇÃO - criar novo gateway
        await paymentGatewayAPI.create(payload)
      }

      onSuccess()
      onClose()
    } catch (error: any) {
      console.error('Erro ao salvar gateway:', error)

      let errorMessage = existingGateway
        ? 'Erro ao atualizar configuração'
        : 'Erro ao salvar configuração'

      if (error.response?.data?.detail) {
        const detail = error.response.data.detail
        if (detail.includes('already configured')) {
          errorMessage = '⚠️ Gateway ASAAS já configurado para este tenant. Use o botão "Configurar" para editar.'
        } else {
          errorMessage = detail
        }
      }

      setTestResult({
        success: false,
        message: errorMessage
      })
    } finally {
      setSaving(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Overlay */}
        <div className="fixed inset-0 bg-gray-900 bg-opacity-75 transition-opacity" onClick={onClose} />

        {/* Modal */}
        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0 bg-white rounded-lg p-2">
                  <img
                    src="https://asaas.com/favicon.ico"
                    alt="ASAAS"
                    className="h-8 w-8"
                    onError={(e) => {
                      e.currentTarget.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%234F46E5"><path d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5z"/></svg>'
                    }}
                  />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">
                    {existingGateway ? 'Editar' : 'Configurar'} ASAAS
                  </h3>
                  <p className="text-sm text-blue-100">Gateway de Pagamento Brasileiro</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Progress Steps */}
            <div className="mt-6">
              <div className="flex items-center justify-between">
                {[
                  { num: 1, label: 'Credenciais' },
                  { num: 2, label: 'Funcionalidades' },
                  { num: 3, label: 'Confirmação' }
                ].map((s, idx) => (
                  <React.Fragment key={s.num}>
                    <div className="flex flex-col items-center">
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
                          step >= s.num
                            ? 'bg-white text-blue-600'
                            : 'bg-blue-500 text-white opacity-60'
                        }`}
                      >
                        {step > s.num ? (
                          <CheckCircleIcon className="h-6 w-6" />
                        ) : step === s.num ? (
                          s.num
                        ) : (
                          s.num
                        )}
                      </div>
                      <span className="mt-1 text-xs text-white">{s.label}</span>
                    </div>
                    {idx < 2 && (
                      <div className={`flex-1 h-1 mx-2 rounded ${step > s.num ? 'bg-white' : 'bg-blue-500 opacity-30'}`} />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="px-6 py-6 bg-gray-50 dark:bg-gray-900">
            {/* Step 1: Credentials */}
            {step === 1 && (
              <div className="space-y-6">
                {/* Info Banner */}
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <div className="flex">
                    <InformationCircleIcon className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                    <div className="ml-3">
                      <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200">Como obter sua API Key ASAAS</h4>
                      <p className="mt-1 text-sm text-blue-700 dark:text-blue-300">
                        1. Acesse sua conta ASAAS → Integrações → API<br />
                        2. Copie sua chave de API (começa com $aact_...)<br />
                        3. Escolha o ambiente: <strong>Sandbox</strong> para testes ou <strong>Produção</strong> para cobranças reais
                      </p>
                    </div>
                  </div>
                </div>

                {/* Form Fields */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Nome da Integração *
                  </label>
                  <input
                    type="text"
                    value={formData.gateway_name}
                    onChange={(e) => setFormData({ ...formData, gateway_name: e.target.value })}
                    placeholder="Ex: ASAAS Produção"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white ${
                      errors.gateway_name ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.gateway_name && (
                    <p className="mt-1 text-sm text-red-600">{errors.gateway_name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    API Key {existingGateway ? '(deixe em branco para manter a atual)' : '*'}
                  </label>
                  <input
                    type="password"
                    value={formData.api_key}
                    onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                    placeholder={existingGateway ? "••••••••••••••••" : "$aact_..."}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white font-mono ${
                      errors.api_key ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.api_key && (
                    <p className="mt-1 text-sm text-red-600">{errors.api_key}</p>
                  )}
                  {existingGateway && (
                    <p className="mt-1 text-xs text-gray-500">
                      Por segurança, a API Key atual não é exibida. Preencha apenas se quiser alterá-la.
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Ambiente *
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, environment: 'sandbox' })}
                      className={`p-4 border-2 rounded-lg transition-all ${
                        formData.environment === 'sandbox'
                          ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-300 dark:border-gray-700 hover:border-blue-400'
                      }`}
                    >
                      <div className="flex items-center justify-center space-x-2">
                        <BoltIcon className="h-5 w-5 text-yellow-600" />
                        <span className="font-semibold">Sandbox (Teste)</span>
                      </div>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">Para desenvolvimento</p>
                    </button>

                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, environment: 'production' })}
                      className={`p-4 border-2 rounded-lg transition-all ${
                        formData.environment === 'production'
                          ? 'border-green-600 bg-green-50 dark:bg-green-900/20'
                          : 'border-gray-300 dark:border-gray-700 hover:border-green-400'
                      }`}
                    >
                      <div className="flex items-center justify-center space-x-2">
                        <ShieldCheckIcon className="h-5 w-5 text-green-600" />
                        <span className="font-semibold">Produção</span>
                      </div>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">Cobranças reais</p>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Features */}
            {step === 2 && (
              <div className="space-y-6">
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Habilitar Métodos de Pagamento
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { key: 'enable_pix', label: 'PIX', icon: QrCodeIcon, color: 'blue', desc: 'QR Code dinâmico' },
                      { key: 'enable_boleto', label: 'Boleto', icon: BanknotesIcon, color: 'orange', desc: 'Bancário registrado' },
                      { key: 'enable_credit_card', label: 'Cartão de Crédito', icon: CreditCardIcon, color: 'purple', desc: 'À vista/parcelado' },
                      { key: 'enable_debit_card', label: 'Cartão de Débito', icon: CreditCardIcon, color: 'green', desc: 'Débito online' }
                    ].map((method) => {
                      const Icon = method.icon
                      return (
                        <button
                          key={method.key}
                          type="button"
                          onClick={() => setFormData({ ...formData, [method.key]: !formData[method.key as keyof FormData] })}
                          className={`p-4 border-2 rounded-lg transition-all text-left ${
                            formData[method.key as keyof FormData]
                              ? `border-${method.color}-600 bg-${method.color}-50 dark:bg-${method.color}-900/20`
                              : 'border-gray-300 dark:border-gray-700 hover:border-gray-400'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-center space-x-2">
                              <Icon className={`h-5 w-5 text-${method.color}-600`} />
                              <span className="font-semibold">{method.label}</span>
                            </div>
                            {formData[method.key as keyof FormData] && (
                              <CheckCircleIcon className={`h-5 w-5 text-${method.color}-600`} />
                            )}
                          </div>
                          <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">{method.desc}</p>
                        </button>
                      )
                    })}
                  </div>
                </div>

                <div>
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Funcionalidades Avançadas
                  </h4>
                  <div className="space-y-3">
                    <label className="flex items-start p-4 border-2 border-gray-300 dark:border-gray-700 rounded-lg hover:border-blue-400 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.enable_split}
                        onChange={(e) => setFormData({ ...formData, enable_split: e.target.checked })}
                        className="mt-1 h-5 w-5 text-blue-600 rounded"
                      />
                      <div className="ml-3">
                        <div className="font-semibold text-gray-900 dark:text-white">Split de Pagamento</div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Dividir pagamentos automaticamente entre múltiplas contas</p>
                      </div>
                    </label>

                    <label className="flex items-start p-4 border-2 border-gray-300 dark:border-gray-700 rounded-lg hover:border-blue-400 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.auto_sync}
                        onChange={(e) => setFormData({ ...formData, auto_sync: e.target.checked })}
                        className="mt-1 h-5 w-5 text-blue-600 rounded"
                      />
                      <div className="ml-3">
                        <div className="font-semibold text-gray-900 dark:text-white">Sincronização Automática</div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Sincronizar transações automaticamente via webhook</p>
                      </div>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Webhook URL (Opcional)
                  </label>
                  <input
                    type="url"
                    value={formData.webhook_url}
                    onChange={(e) => setFormData({ ...formData, webhook_url: e.target.value })}
                    placeholder="https://seusite.com/webhook/asaas"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white ${
                      errors.webhook_url ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.webhook_url && (
                    <p className="mt-1 text-sm text-red-600">{errors.webhook_url}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">Receba notificações em tempo real sobre pagamentos</p>
                </div>
              </div>
            )}

            {/* Step 3: Test & Confirmation */}
            {step === 3 && (
              <div className="space-y-6">
                <div className="text-center py-8">
                  <SparklesIcon className="h-16 w-16 text-blue-600 mx-auto mb-4" />
                  <h4 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    Configuração Pronta!
                  </h4>
                  <p className="text-gray-600 dark:text-gray-400">
                    Revise as configurações antes de salvar
                  </p>
                </div>

                {/* Summary */}
                <div className="bg-white dark:bg-gray-800 rounded-lg border-2 border-gray-200 dark:border-gray-700 p-6">
                  <h5 className="font-semibold text-gray-900 dark:text-white mb-4">Resumo da Configuração</h5>
                  <dl className="space-y-3">
                    <div className="flex justify-between">
                      <dt className="text-gray-600 dark:text-gray-400">Gateway:</dt>
                      <dd className="font-semibold">ASAAS</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-gray-600 dark:text-gray-400">Nome:</dt>
                      <dd className="font-semibold">{formData.gateway_name}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-gray-600 dark:text-gray-400">Ambiente:</dt>
                      <dd className="font-semibold capitalize">{formData.environment}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-gray-600 dark:text-gray-400">Métodos Ativos:</dt>
                      <dd className="font-semibold">
                        {[
                          formData.enable_pix && 'PIX',
                          formData.enable_boleto && 'Boleto',
                          formData.enable_credit_card && 'Cartão Crédito',
                          formData.enable_debit_card && 'Cartão Débito'
                        ].filter(Boolean).join(', ')}
                      </dd>
                    </div>
                  </dl>
                </div>

                {testResult && (
                  <div className={`p-4 rounded-lg ${testResult.success ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'}`}>
                    <div className="flex items-start">
                      {testResult.success ? (
                        <CheckCircleIcon className="h-5 w-5 text-green-600 flex-shrink-0" />
                      ) : (
                        <ExclamationCircleIcon className="h-5 w-5 text-red-600 flex-shrink-0" />
                      )}
                      <p className={`ml-3 text-sm ${testResult.success ? 'text-green-800 dark:text-green-200' : 'text-red-800 dark:text-red-200'}`}>
                        {testResult.message}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-100 dark:bg-gray-800 px-6 py-4 border-t dark:border-gray-700">
            {/* Validation Errors Display */}
            {Object.keys(errors).length > 0 && step === 1 && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <div className="flex items-start">
                  <ExclamationCircleIcon className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="ml-3">
                    <h4 className="text-sm font-semibold text-red-800 dark:text-red-200">Erros de validação:</h4>
                    <ul className="mt-1 text-sm text-red-700 dark:text-red-300 list-disc list-inside">
                      {Object.values(errors).map((error, idx) => (
                        <li key={idx}>{error}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between">
              <button
                onClick={() => step > 1 ? setStep(step - 1) : onClose()}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                {step === 1 ? 'Cancelar' : 'Voltar'}
              </button>

              <div className="flex space-x-3">
                {step < 3 && (
                  <button
                    onClick={() => {
                      if (step === 1) {
                        handleTestConnection()
                      } else if (step === 2) {
                        setStep(3)
                      }
                    }}
                    disabled={testing}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
                  >
                    {testing && step === 1 && (
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                    )}
                    <span>{step === 1 ? (testing ? 'Testando...' : 'Testar e Continuar') : 'Próximo'}</span>
                  </button>
                )}

                {step === 3 && (
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
                  >
                    {saving && (
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                    )}
                    <span>{saving ? 'Salvando...' : 'Salvar Configuração'}</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AsaasConfigModal
